import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { attachMuxMonitor } from "@/lib/mux";

/**
 * useLatest — stores the freshest value in a ref so long-lived effects can
 * read it without needing to depend on it. Prevents "prop identity changes
 * every render → useEffect re-fires → setState → infinite loop" (React #185).
 */
function useLatest(value) {
  const ref = useRef(value);
  useEffect(() => { ref.current = value; });
  return ref;
}

/**
 * useLiveKitToken — POSTs /livekit/token/{id} once when streamId changes and
 * exposes `{token, serverUrl, role, err, refresh}`. Calling `refresh()`
 * re-mints the token (needed after a raise-hand request is accepted so the
 * guest transitions viewer → co-host without a page reload).
 */
export function useLiveKitToken(streamId, { endpoint } = {}) {
  const [token, setToken] = useState(null);
  const [serverUrl, setServerUrl] = useState(null);
  const [role, setRole] = useState(null);
  const [err, setErr] = useState("");
  const [paywall, setPaywall] = useState(null); // { price } when server answers 402 paid_entry_required
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = endpoint || `/livekit/token/${streamId}`;
        const { data } = await api.post(url);
        if (cancelled) return;
        setPaywall(null);
        setToken(data.token);
        setServerUrl(data.server_url);
        setRole(data.role);
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 402 && d?.code === "paid_entry_required") { setPaywall({ price: d.price }); return; }
        setErr(typeof d === "string" ? d : d?.message || "Could not join stream.");
      }
    })();
    return () => { cancelled = true; };
  }, [streamId, endpoint, refreshTick]);

  const refresh = useCallback(() => setRefreshTick((n) => n + 1), []);
  return { token, serverUrl, role, err, paywall, refresh };
}

/**
 * useCohostStatusWatch — no second socket, no poll. A `cohost:accept` /
 * `cohost:invite` frame for THIS viewer on the shared room socket (or a
 * reconnect `sync`) triggers ONE status check.
 */
export function useCohostStatusWatch({ isHost, isCohost, streamId, userId, onAccepted, socket }) {
  const onAcceptedRef = useLatest(onAccepted);
  useEffect(() => {
    if (isHost || isCohost || !streamId || !socket) return undefined;
    let cancelled = false;
    let seenAccepted = false;
    const check = async () => {
      if (cancelled || seenAccepted) return;
      try {
        const { data } = await api.get(`/live/${streamId}/cohost/my-status`);
        if (data?.is_cohost && !seenAccepted) {
          seenAccepted = true;
          onAcceptedRef.current?.();
        }
      } catch { /* transient */ }
    };
    check();
    let firstSync = true;
    const offs = [
      socket.subscribe("cohost:accept", (m) => { if (m?.requester_id === userId) check(); }),
      socket.subscribe("cohost:invite", (m) => { if (m?.invitee_id === userId) check(); }),
      // Re-check only after a RECONNECT (the first hello is covered by check() above).
      socket.subscribe("sync", () => { if (firstSync) { firstSync = false; return; } check(); }),
    ];
    return () => { cancelled = true; offs.forEach((off) => off()); };
  }, [isHost, isCohost, streamId, userId, onAcceptedRef, socket]);
}

/** Host AFK state now rides on the WS `pong` (host heartbeat) + `brb:changed` + `sync`. */
export function useHostAfkFromSocket({ socket, onAfk }) {
  const onAfkRef = useLatest(onAfk);
  useEffect(() => {
    if (!socket) return undefined;
    const offs = [
      socket.subscribe("pong", (m) => { if (m?.afk) onAfkRef.current?.(m.afk); }),
      socket.subscribe("sync", (s) => { if (s?.afk) onAfkRef.current?.(s.afk); }),
    ];
    return () => offs.forEach((off) => off());
  }, [socket, onAfkRef]);
}

/** Viewers list — snapshot + participant:join / participant:leave (no poll). */
export function useViewersList({ socket }) {
  const [viewers, setViewers] = useState([]);
  useEffect(() => {
    if (!socket) return undefined;
    const offs = [
      socket.subscribe("sync", (s) => { if (Array.isArray(s?.participants)) setViewers(s.participants); }),
      socket.subscribe("participant:join", (m) => {
        const p = m?.participant; if (!p?.user_id) return;
        setViewers((cur) => (cur.some((v) => v.user_id === p.user_id) ? cur : [p, ...cur]));
      }),
      socket.subscribe("participant:leave", (m) => {
        if (!m?.user_id) return;
        setViewers((cur) => cur.filter((v) => v.user_id !== m.user_id));
      }),
      socket.subscribe("participants:delta", (m) => {
        const left = new Set(m?.left || []);
        const joined = (m?.joined || []).filter((p) => p?.user_id);
        setViewers((cur) => {
          const keep = cur.filter((v) => !left.has(v.user_id));
          const have = new Set(keep.map((v) => v.user_id));
          return [...joined.filter((p) => !have.has(p.user_id)), ...keep];
        });
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [socket]);
  return viewers;
}

/** Fresh stream doc — snapshot.stream + live:update / brb:changed / stream:lock / gift goal ticks. */
export function useFreshStream(stream, socket) {
  const [freshStream, setFreshStream] = useState(stream);
  useEffect(() => {
    if (!socket) return undefined;
    const offs = [
      socket.subscribe("sync", (s) => { if (s?.stream) setFreshStream((cur) => ({ ...(cur || {}), ...s.stream })); }),
      socket.subscribe("live:update", (m) => { if (m?.stream) setFreshStream((cur) => ({ ...(cur || {}), ...m.stream })); }),
      socket.subscribe("stream:lock", (m) => setFreshStream((cur) => (cur ? { ...cur, is_locked: !!m?.locked } : cur))),
      socket.subscribe("brb:changed", (m) => setFreshStream((cur) => (cur ? { ...cur, brb_active: !!m?.brb_active, brb_started_at: m?.brb_started_at ?? null } : cur))),
      socket.subscribe("stage:update", (m) => { if (m?.stage) setFreshStream((cur) => (cur ? { ...cur, stage: m.stage } : cur)); }),
      socket.subscribe("gift:sent", (m) => {
        if (typeof m?.goal_progress !== "number") return;
        setFreshStream((cur) => (cur ? { ...cur, goal_progress: m.goal_progress, goal_coins: m.goal_coins ?? cur.goal_coins } : cur));
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [socket]);
  return freshStream;
}

/** Nearest OTHER live stream, refreshed every 15s (discovery — not room state). */
export function useNextLive({ streamId, userLocation, enabled }) {
  const [nextLive, setNextLive] = useState(null);
  const lat = userLocation?.lat ?? 40.7128;
  const lng = userLocation?.lng ?? -74.0060;
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/nearby/next-live", {
          params: { lat, lng, exclude: streamId },
        });
        if (!cancelled) setNextLive(data);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [streamId, lat, lng, enabled]);
  return nextLive;
}

/**
 * Battle state — snapshot.battle on connect/reconnect; battle:score /
 * battle:end patch in place; lifecycle frames (challenge/accept/decline)
 * trigger ONE reload. `reload` stays available for optimistic callers.
 */
export function useBattlePoll(streamId, socket) {
  const [battle, setBattle] = useState(null);
  const loadBattle = useCallback(async () => {
    try {
      const { data } = await api.get(`/live/${streamId}/battle`);
      setBattle(data);
    } catch { setBattle(null); }
  }, [streamId]);
  useEffect(() => {
    if (!socket) return undefined;
    const offs = [
      socket.subscribe("sync", (s) => { if (s && "battle" in s) setBattle(s.battle); }),
      socket.subscribe("battle:accept", loadBattle),
      socket.subscribe("battle:challenge", loadBattle),
      socket.subscribe("battle:decline", loadBattle),
      socket.subscribe("battle:end", loadBattle),
    ];
    return () => offs.forEach((off) => off());
  }, [socket, loadBattle]);
  return { battle, setBattle, reload: loadBattle };
}

/**
 * Attaches mux-embed to every <video> tile that LiveKit mounts inside `stageRef`.
 * Watches the root with MutationObserver, cleans up on unmount / room close.
 */
export function useMuxLiveTracking({ muxEnvKey, token, stageRef, stream, userId }) {
  const attachedRef = useRef(new WeakMap());
  useEffect(() => {
    if (!muxEnvKey || !token || !stageRef.current) return;
    const root = stageRef.current;
    const attached = attachedRef.current;
    const attach = (v) => {
      if (!v || attached.has(v)) return;
      const cleanup = attachMuxMonitor(v, {
        envKey: muxEnvKey,
        video_id: stream.id,
        video_title: stream.title || `Live · ${stream.host?.display_name || "unknown"}`,
        video_stream_type: "live",
        video_content_type: "livekit",
        viewer_user_id: userId || "anon",
        player_name: "My Panda X Web Live",
        page_type: "watchpage",
        sub_property_id: stream.host?.id || undefined,
      });
      attached.set(v, cleanup);
    };
    root.querySelectorAll("video").forEach(attach);
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeName === "VIDEO") attach(n);
          else if (n.querySelectorAll) n.querySelectorAll("video").forEach(attach);
        });
        m.removedNodes.forEach((n) => {
          if (n.nodeName === "VIDEO" && attached.has(n)) {
            try { attached.get(n)(); } catch { /* noop */ }
            attached.delete(n);
          }
        });
      }
    });
    obs.observe(root, { childList: true, subtree: true });
    return () => {
      obs.disconnect();
      root.querySelectorAll("video").forEach((v) => {
        const c = attached.get(v);
        if (c) { try { c(); } catch { /* noop */ } attached.delete(v); }
      });
    };
  }, [muxEnvKey, token, stageRef, stream.id, stream.title, stream.host?.display_name, stream.host?.id, userId]);
}
